import { dorotheeVoice } from "@/lib/dorotheeVoice";
import { pickPhrase } from "@/lib/dorotheePhrases";
import { useEffect, useRef, useState, useCallback } from "react";
import { BookOpen, Feather, Layers, ScrollText, Sparkles, Volume2 } from "lucide-react";
import { IMG } from "@/lib/images";
import { useSound } from "@/lib/SoundContext";

const PROPS = {
  history: { Icon: ScrollText }, medium: { Icon: Feather }, tarot: { Icon: Layers },
  legend: { Icon: ScrollText }, mystic: { Icon: BookOpen }, default: { Icon: Sparkles },
};

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export default function DorotheeIA({ thinking, mood = "default", speaking, intervention }) {
  const PropIcon = (PROPS[mood] || PROPS.default).Icon;
  const { muted } = useSound() || {};
  const headRef = useRef(null);
  const subtitleTimer = useRef(null);
  const [bubble, setBubble] = useState(null);
  const [show, setShow] = useState(false);
  const [subtitle, setSubtitle] = useState(null);
  const [talking, setTalking] = useState(false);
  const [catTalk, setCatTalk] = useState(null);
  const exch = useRef({ running: false, until: 0 });
  const busyRef = useRef(false);
  useEffect(() => { busyRef.current = !!(thinking || speaking || talking); }, [thinking, speaking, talking]);

  const speakRef = useRef(null);

  // Vrai échange à tour de rôle entre Dorothée et le chat quand il s'approche.
  const runExchange = useCallback((voiced = false) => {
    const now = Date.now();
    if (exch.current.running || (!voiced && now < exch.current.until) || busyRef.current) return;
    exch.current.running = true;
    const DIALOGS = [
      [["d", "Viens là, toi…", 2400], ["c", "Miaou ?", 1700], ["d", "Oui, tu peux rester près de moi.", 2500], ["c", "prrr…", 1800]],
      [["d", "Tu veux participer à la consultation ?", 2700], ["c", "Miaou !", 1600], ["d", "Bon… c'est toi la vedette, alors.", 2500]],
      [["d", "Doucement, mon grand.", 2200], ["c", "prrr…", 1700], ["d", "Voilà… tout doux.", 2200]],
    ];
    const dlg = DIALOGS[Math.floor(Math.random() * DIALOGS.length)];
    const total = dlg.reduce((a, t) => a + t[2], 0);
    window.dispatchEvent(new CustomEvent("dl-cat-engage", { detail: { duration: total } }));
    let acc = 0;
    dlg.forEach(([who, text, ms]) => {
      setTimeout(() => {
        if (busyRef.current) return;
        if (who === "d") { if (voiced && speakRef.current) speakRef.current(text, true); else setCatTalk(text); }
        else window.dispatchEvent(new CustomEvent("dl-cat-say", { detail: { text, ms: ms + 400 } }));
      }, acc);
      acc += ms;
    });
    setTimeout(() => { setCatTalk(null); exch.current.running = false; exch.current.until = Date.now() + 45000; }, acc + 300);
  }, []);
  const hasSpeech = typeof window !== "undefined" && "Audio" in window;
  useEffect(() => () => { clearTimeout(subtitleTimer.current); dorotheeVoice.stop(); }, []);
  const speak = useCallback((text, force) => {
    clearTimeout(subtitleTimer.current); setSubtitle(text);
    const clear = () => { setTalking(false); setSubtitle(null); };
    if (muted && !force) { subtitleTimer.current = setTimeout(clear, clamp(text.length * 90, 3000, 10000)); return; }
    dorotheeVoice.speak(text, { gesture: !!force, onstart: () => setTalking(true), onend: clear });
  }, [muted]);
  useEffect(() => {
    if (!intervention) return;
    setSubtitle(intervention);
    clearTimeout(subtitleTimer.current);
    subtitleTimer.current = setTimeout(() => setSubtitle(null), clamp(intervention.length * 100, 5000, 13000));
  }, [intervention]);

  const onAvatarClick = () => {
    speakRef.current = speak;
    const cat = document.getElementById("dl-cat-wrap");
    const catVisible = cat && parseFloat(getComputedStyle(cat).opacity || "0") > 0.05;
    if (catVisible) { exch.current.until = 0; runExchange(true); }
    else speak(pickPhrase("click") || "Bon… raconte, je t’écoute.", true);
  };

  // Suivi naturel du chat.
  useEffect(() => {
    let raf, last = 0;
    const rest = () => { if (headRef.current) headRef.current.style.transform = "perspective(760px) rotateY(0deg) rotate(0deg)"; };
    const tick = (t) => {
      raf = requestAnimationFrame(tick);
      if (t - last < 110) return; last = t;
      const head = headRef.current; if (!head) return;
      if (thinking || speaking || talking) { rest(); return; }
      const cat = document.getElementById("dl-cat-wrap");
      const visible = cat && parseFloat(getComputedStyle(cat).opacity || "0") > 0.05;
      const arect = head.getBoundingClientRect();
      if (!visible || !arect.width) { rest(); return; }
      const crect = cat.getBoundingClientRect();
      const dx = crect.left + crect.width / 2 - (arect.left + arect.width / 2);
      const dy = crect.top + crect.height / 2 - (arect.top + arect.height / 2);
      const ry = clamp((dx / 700) * 15, -15, 15);
      let tilt = -ry * 0.18, leanY = 0, scale = 1;
      if (Math.abs(dx) < arect.width * 1.15 && dy > 30) { tilt = ry >= 0 ? 5 : -5; leanY = 7; scale = 1.02; }
      if (Math.abs(dx) < arect.width * 1.4 && dy > 10) runExchange();
      head.style.transform = `perspective(760px) rotateY(${ry}deg) rotate(${tilt}deg) translateY(${leanY}px) scale(${scale})`;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [thinking, speaking, talking]);

  // Bulle de présence (veille uniquement).
  useEffect(() => {
    let tShow, tHide, tNext, stopped = false;
    const pick = () => pickPhrase("idle");
    const cycle = () => {
      if (stopped) return;
      const phrase = pick(); if (phrase) { setBubble(phrase); setShow(true); }
      tHide = setTimeout(() => { setShow(false); tNext = setTimeout(() => { setBubble(null); tShow = setTimeout(cycle, rand(35000, 65000)); }, 600); }, rand(10000, 15000));
    };
    if (thinking || speaking || talking || subtitle || catTalk) { setShow(false); setBubble(null); return () => {}; }
    tShow = setTimeout(cycle, 3000);
    return () => { stopped = true; clearTimeout(tShow); clearTimeout(tHide); clearTimeout(tNext); };
  }, [thinking, speaking, talking, subtitle, catTalk]);

  const isSpeaking = speaking || talking;
  const bubbleText = subtitle || catTalk || (show && !isSpeaking && !thinking && !catTalk ? bubble : null);
  const bubbleTid = subtitle ? "ia-subtitle" : catTalk ? "ia-cat-talk" : "ia-presence-bubble";

  return (
    <div className="relative w-full max-w-[340px] mx-auto select-none" data-testid="dorothee-ia-character">
      <div className="dl-ia-glow absolute -inset-4 rounded-[24px]"
        style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(212,185,120,0.4), transparent 68%)", filter: "blur(26px)" }} />

      {[0, 1, 2].map((i) => (
        <span key={i} className="dl-particle absolute w-1.5 h-1.5 rounded-full bg-[#D4B978]"
          style={{ left: `${28 + i * 20}%`, bottom: "26%", animationDelay: `${i * 1.3}s`, boxShadow: "0 0 8px #D4B978" }} />
      ))}

      {thinking && (
        <div className="absolute -top-2 right-2 z-20 flex flex-col items-center">
          <span className="dl-ia-prop w-11 h-11 flex items-center justify-center text-[#D4B978]"><PropIcon size={30} /></span>
        </div>
      )}

      {bubbleText && (
        <div data-testid={bubbleTid}
          className="absolute z-30 left-1/2 -translate-x-1/2 top-6 w-[min(280px,88vw)] text-center"
          style={{ opacity: subtitle || catTalk ? 1 : show ? 1 : 0, transition: "opacity .5s ease", pointerEvents: "none" }}>
          <div className="inline-block bg-[#050814]/92 border border-[rgba(198,167,106,0.4)] px-5 py-3 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.45)]">
            <span className="serif text-[#F3E9D2] text-xl font-semibold leading-snug">{bubbleText}</span>
          </div>
          <span className="block mx-auto w-2.5 h-2.5 -mt-1.5 rotate-45 bg-[#050814]/92 border-r border-b border-[rgba(198,167,106,0.4)]" />
        </div>
      )}

      <div className={`dl-ia-sway relative z-10 ${isSpeaking ? "dl-ia-speaking" : ""}`}>
        <div className={`dl-ia-breathe relative border cursor-pointer transition-shadow ${isSpeaking ? "border-[#D4B978] shadow-[0_0_34px_rgba(212,185,120,0.45)]" : "border-[rgba(198,167,106,0.4)]"}`}
          style={{ borderRadius: 14 }}
          onClick={onAvatarClick}
          role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onAvatarClick(); }}
          data-testid="ia-avatar-speak"
          title="Cliquez pour m'entendre">
          <div ref={headRef} className="relative overflow-hidden"
            style={{ borderRadius: 14, transformStyle: "preserve-3d", transformOrigin: "50% 42%", transition: "transform .55s cubic-bezier(.22,.61,.36,1)", willChange: "transform" }}>
            <img src={IMG.iaPortrait} alt="Dorothée IA, portrait illustré" className="w-full object-cover" />
            <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 -40px 60px rgba(5,8,20,0.6)" }} />
          </div>
          {hasSpeech && !muted && (
            <span className="absolute bottom-2 right-2 z-10 w-8 h-8 rounded-full bg-[#050814]/80 border border-[rgba(198,167,106,0.5)] flex items-center justify-center text-[#D4B978]">
              <Volume2 size={15} />
            </span>
          )}
        </div>
      </div>

      {thinking && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 bg-[#050814]/85 border border-[rgba(198,167,106,0.3)] px-4 py-2">
          <span className="dl-think-dot w-1.5 h-1.5 rounded-full bg-[#D4B978]" style={{ animationDelay: "0s" }} />
          <span className="dl-think-dot w-1.5 h-1.5 rounded-full bg-[#D4B978]" style={{ animationDelay: ".2s" }} />
          <span className="dl-think-dot w-1.5 h-1.5 rounded-full bg-[#D4B978]" style={{ animationDelay: ".4s" }} />
        </div>
      )}
    </div>
  );
}
